/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import Modal from "components/Modal";
import UpdateProfile from "./UpdateProfile";
import GenericToaster from "components/Toaster";
import appConfig from "services/apiConfig";
import apiService from "services/apiService";
import UpdateDocs from "./UpdateDocs";
import ProfileCard from "./ProfileCard";
import ProfileHeader from "./ProfileHeader";
import { PROFILE_TAB_LABEL } from "./headerActionsSlot";
import UserProjects from "./UserProjects";
import Tabs from "components/Tab/Tabs";
import DocumentList from "./DocumentList";
import EmployeeWorkSummary from "./EmployeeWorkSummary";
import LeaveSummary from "./LeaveSummary";
import { compressImage } from "util/Util";
import { storageService } from "services/storageService";
import SkillManagement from "./skills/SkillManagement";
import { useToaster } from "contexts/toasterContext";
import { isOnboardingStatus } from "util/userStatus";
import { useSearchParams } from "react-router-dom";
import { useNotificationBadgeData } from "contexts/notificationBadgeContext";
import { getFlaggedSubTabs } from "services/Notification/notificationInterface";

export default function ProfileDetails({ user, setRefresh }: any) {
  const [openModal, setOpenModal] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [localUser, setLocalUser] = useState(user);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  //uploading image 
  const [imageUploading, setImageUploading] = useState(false);
  //
  const revokePreviewRef = useRef<string | null>(null);
  const { addToast } = useToaster();

  // Notification badge (shared from the Sidebar) → which profile sub-tabs are
  // flagged, plus the ?subtab= deep-link target used when a notification is clicked.
  const notificationBadge = useNotificationBadgeData();
  const flaggedSubTabs = useMemo(
    () => getFlaggedSubTabs(notificationBadge, "profile"),
    [notificationBadge],
  );
  const [searchParams] = useSearchParams();
  const subTabParam = searchParams.get("subtab");

  const handleClick = useCallback(() => {
    setOpenModal(true);
  }, []);

  const handleUserUpdated = useCallback((updatedUser: any) => {
    setLocalUser((prev: any) => ({ ...prev, ...updatedUser }));
  }, []);
  const [toastData, setToastData] = useState<{
    message: string;
    severity: "info" | "error";
  } | null>(null);

  //sonar cognitive issue fix 
  const getCompressedFile = async (file: File) => {
    if (file.size <= 5 * 1024 * 1024) {
      return file;
    }

    try {
      return await compressImage(file, 5);
    } catch (err) {
      console.error("Compression failed", err);
      return file;
    }
  };

  const updatePreviewImage = (file: File) => {
    if (revokePreviewRef.current) {
      URL.revokeObjectURL(revokePreviewRef.current);
    }

    const previewUrl = URL.createObjectURL(file);

    setPreviewImage(previewUrl);
    revokePreviewRef.current = previewUrl;
  };

  const getStorageType = async () => {
    try {
      const storageRes = await apiService.get("api/admin/storage");
      return storageRes?.data?.storage || "DRIVE";
    } catch (err) {
      console.log("Failed to detect storage type, falling back to DRIVE", err);
      return "DRIVE";
    }
  };

  const updateLocalUserImage = (imageUrl: string) => {
    setLocalUser((prev: any) => {
      const updated = {
        ...prev,
        imageUrl,
      };

      storageService.setItem("user", JSON.stringify(updated));

      window.dispatchEvent(
        new CustomEvent("user-profile-updated", {
          detail: { imageUrl },
        })
      );

      return updated;
    });

    setPreviewImage(null);
  };

  const handleUploadError = (err: any) => {
    console.log("User Profile Image Not upload : ", err);

    if (err instanceof TypeError) {
      setToastData({
        message: "413 Request Entity Too Large",
        severity: "error",
      });

      return;
    }

    setToastData({
      message: "Failed To Upload Profile Image",
      severity: "error",
    });
  };

  const handleUploadSuccess = (imageUrl?: string) => {
    setRefresh("updated");

    if (imageUrl) {
      updateLocalUserImage(imageUrl);
    }

    setToastData({
      message: "Profile Image Updated Successfully",
      severity: "info",
    });
  };

  const uploadToDrive = async (
    formData: FormData,
    tokenValue: string,
    userId: string
  ) => {
    const response = await fetch(
      `${appConfig.getBaseUrl()}api/image/${userId}`,
      {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${tokenValue}`,
        },
      }
    );

    if (response.status === 401) {
      console.log("Unauthorized access, removing token and redirecting to login");
      window.localStorage.removeItem("accessToken");
      throw new Error("Unauthorized");
    }

    if (!response.ok) {
      throw new Error("Upload failed");
    }

    return response.json();
  };

  const uploadToS3 = async (
    file: File,
    tokenValue: string,
    userId: string
  ) => {
    const uploadUrlRes = await apiService.post("api/upload-url", {
      fileName: file.name,
    });

    const { uploadUrl, key } = uploadUrlRes.data || {};

    if (!uploadUrl || !key) {
      throw new Error("Invalid upload URL response");
    }

    const putResponse = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
    });

    if (!putResponse.ok) {
      throw new Error("Upload failed");
    }

    const imageKeyFormData = new FormData();
    imageKeyFormData.append("key", key);

    const mappedImageResponse = await fetch(
      `${appConfig.getBaseUrl()}api/image/${userId}`,
      {
        method: "POST",
        body: imageKeyFormData,
        headers: {
          Authorization: `Bearer ${tokenValue}`,
        },
      }
    );

    if (mappedImageResponse.status === 401) {
      window.localStorage.removeItem("accessToken");
      throw new Error("Unauthorized");
    }

    if (!mappedImageResponse.ok) {
      throw new Error("Upload failed");
    }

    return mappedImageResponse.json();
  };
  //


  // Receives the cropped File from ProfileCard's crop modal and uploads it
  const updateProfileImage = async (selectedFile: File) => {
    if (!localUser?.id) {
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/bmp", "image/tiff"];
    if (!allowedTypes.includes(selectedFile.type)) {
      addToast("Only JPEG, JPG, PNG, BMP, and TIFF formats are allowed", "error");
      //comment added for pushing with updated changes
      // Removed e.target.value = null;
      return;
    }

    if (selectedFile.size > 2 * 1024 * 1024) {
      addToast("Please select a file of size smaller than 2MB", "error");
      // Removed e.target.value = null;
      return;
    }

    setImageUploading(true);

    try {
      const file = await getCompressedFile(selectedFile);
      updatePreviewImage(file);

      const tokenValue = window.localStorage.getItem("accessToken") || "";
      const storageType = await getStorageType();

      let response;

      if (storageType === "S3") {
        response = await uploadToS3(file, tokenValue, localUser.id);
      } else {
        const formData = new FormData();
        formData.append("image", file);
        response = await uploadToDrive(formData, tokenValue, localUser.id);
      }

      handleUploadSuccess(response?.imageUrl);
    } catch (err) {
      handleUploadError(err);
    } finally {
      setImageUploading(false);
    }
  };
  const tabData = [
    {
      label: "Dashboard",
      content: <EmployeeWorkSummary user={localUser} />,
    },
    {
      label: "Profile",
      content: (
        <ProfileCard
          user={localUser}
          onEditPersonal={handleClick}
          onUserUpdated={handleUserUpdated}
        />
      ),
    },
    {
      label: "Projects",
      content: <UserProjects user={localUser} />,
    },
    {
      label: "Leave",
      content: <LeaveSummary user={localUser} />,
    },

    // {
    //   label: "View Document",
    //   content: <ViewDocs user={localUser} />,
    // },
    {
      label: "Documents",
      content: <UpdateDocs />,
    },
  ];
  localUser.role === 2 &&
    tabData.push(
      {
        label: "Add Skills",
        // content: <AddSkills></AddSkills>,
        content: <SkillManagement ></SkillManagement>
      },
      {
        label: "Document Form",
        content: <DocumentList />,
      }
    );

  // ONBOARDING users may only see the Profile and Documents sub-tabs. For every
  // other status the full tab set is shown, so existing behaviour is unchanged.
  const ONBOARDING_ALLOWED_TABS = ["Profile", "Documents"];
  const baseTabs = isOnboardingStatus(localUser?.userStatus)
    ? tabData.filter((tab) => ONBOARDING_ALLOWED_TABS.includes(tab.label))
    : tabData;

  // Flag sub-tabs that have matching notifications (Profile / Documents) with a dot.
  const visibleTabs = baseTabs.map((tab) => ({
    ...tab,
    showIndicator: flaggedSubTabs.has(tab.label),
  }));

  // Deep-link: when a notification is clicked it navigates here with ?subtab=,
  // so open that sub-tab. Depends only on primitives (the param + the tab-label
  // list) so it re-applies if the tabs change but never fights manual tab clicks.
  const tabLabelsKey = visibleTabs.map((tab) => tab.label).join("|");
  useEffect(() => {
    if (!subTabParam) return;
    const index = tabLabelsKey
      .split("|")
      .findIndex((label) => label.toLowerCase() === subTabParam.toLowerCase());
    if (index >= 0) setActiveTabIndex(index);
  }, [subTabParam, tabLabelsKey]);

  return (
    // Fill the Sidebar layout's content area (h-[calc(100vh-70px)]) as a flex column.
    // overflow-hidden prevents this container from scrolling; only the inner panel scrolls.
    <div className="flex flex-col h-full overflow-hidden text-slate-900 dark:text-white transition-colors duration-300">

      {/* ── Fixed header — profile photo, name, designation, edit button.
           flex-shrink-0 ensures it never compresses or scrolls away. ── */}
      <div className="flex-shrink-0 w-full pt-2 px-4 pb-0">
        <ProfileHeader
          user={localUser}
          onImageFileReady={updateProfileImage}
          previewImage={previewImage}
          imageUploading={imageUploading}
          onUpdateProfile={handleClick}
          showEditProfile={visibleTabs[activeTabIndex]?.label === PROFILE_TAB_LABEL}
        />
      </div>

      {/* ── Scrollable tab section — only this area scrolls vertically.
           min-h-0 is required so flex-1 can shrink below its content size. ── */}
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col relative">
        <div className="w-full py-0 pl-4 flex-1 min-h-0 flex flex-col">
          <Tabs
            contentClassName={`overflow-y-auto ${
              ["Dashboard", "Documents","Add Skills"].includes(visibleTabs[activeTabIndex]?.label)
                ? "pt-0"
                : "pt-2"
            }`}
            tabs={visibleTabs}
            activeIndex={activeTabIndex}
            onTabChange={setActiveTabIndex}
          />
        </div>
      </div>

      {openModal && (
        <Modal
          isOpen={openModal}
          onClose={() => setOpenModal(false)}
          header="Edit Profile Details"
          custemHeight="700px"
          customWidth="700px"
        >
          <UpdateProfile
            data={localUser}
            onClose={() => setOpenModal(false)}
            setToastData={setToastData}
            setRefresh={(val: any, updatedUser?: any) => {
              setRefresh(val);
              if (updatedUser) setLocalUser((prev: any) => ({ ...prev, ...updatedUser }));
            }}
          />
        </Modal>
      )}

      {toastData && (
        <GenericToaster
          open={true}
          message={toastData.message}
          severity={toastData.severity}
          onClose={() => setToastData(null)}
        />
      )}
    </div>
  );
}
